import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Music, Pause, X, Play, Heart, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface AudioProgressBarProps {
  trackName: string;
  artistName: string;
  imageUrl: string;
  progress: number; // 0-100
  duration: number; // in seconds
  currentTime: number; // in seconds
  source: 'spotify' | 'youtube';
  onStop: () => void;
  onTogglePlayPause?: () => void;
  isPlaying?: boolean;
  isAISuggestion?: boolean;
  onAddToSelections?: () => Promise<void>;
}

export const AudioProgressBar = ({
  trackName,
  artistName,
  imageUrl,
  progress,
  duration,
  currentTime,
  source,
  onStop,
  onTogglePlayPause,
  isPlaying = true,
  isAISuggestion = false,
  onAddToSelections,
}: AudioProgressBarProps) => {
  const { toast } = useToast();
  const [isAdded, setIsAdded] = useState(false);
  const [showAddPrompt, setShowAddPrompt] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate waveform bars that react to music
  const [waveformBars, setWaveformBars] = useState<number[]>([]);
  const [isHovering, setIsHovering] = useState(false);
  
  useEffect(() => {
    if (!isPlaying) {
      // Static bars when paused
      setWaveformBars(Array(24).fill(0.3));
      return;
    }
    
    // Create 24 bars around the circle
    const bars = Array.from({ length: 24 }, (_, i) => {
      // Simulate bass response with varying heights based on progress
      const baseHeight = 0.3;
      const variation = Math.sin((currentTime * 10 + i) * 0.5) * 0.4;
      const pulse = Math.sin(currentTime * 8) * 0.3;
      return Math.max(0.2, baseHeight + variation + pulse);
    });
    setWaveformBars(bars);
  }, [currentTime, isPlaying]);

  // Show add prompt for AI suggestions after 5 seconds
  useEffect(() => {
    if (isAISuggestion && !isAdded && isPlaying) {
      const timer = setTimeout(() => {
        setShowAddPrompt(true);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowAddPrompt(false);
    }
  }, [isAISuggestion, isAdded, isPlaying]);

  const handleAddToSelections = async () => {
    if (!onAddToSelections) return;
    
    try {
      await onAddToSelections();
      setIsAdded(true);
      setShowAddPrompt(false);
      toast({
        title: "✨ Added to Your Selections!",
        description: `${trackName} by ${artistName}`,
      });
    } catch (error) {
      console.error('Error adding to selections:', error);
      toast({
        title: "Error",
        description: "Failed to add song",
        variant: "destructive"
      });
    }
  };

  return (
    <TooltipProvider>
      <motion.div
        drag
        dragMomentum={false}
        dragElastic={0.1}
        dragConstraints={{
          top: 0,
          left: 0,
          right: window.innerWidth - 140,
          bottom: window.innerHeight - 140
        }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        initial={{ opacity: 0, scale: 0.8, y: -20 }}
        animate={{ 
          opacity: 1,
          scale: 1, 
          y: 0 
        }}
        exit={{ opacity: 0, scale: 0.8, y: -20 }}
        transition={{ 
          type: "spring", 
          stiffness: 300, 
          damping: 25,
          mass: 0.8
        }}
        className="fixed top-4 right-4 z-50 cursor-move"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Floating Bubble Container - Compact */}
        <div className="relative">
        {/* Magical Glow Effect */}
        <motion.div
          className="absolute inset-0 rounded-full blur-xl opacity-40"
          animate={{
            background: [
              'radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)',
              'radial-gradient(circle, hsl(var(--accent) / 0.4) 0%, transparent 70%)',
              'radial-gradient(circle, hsl(var(--primary) / 0.4) 0%, transparent 70%)',
            ],
            scale: isHovering ? [1, 1.3, 1] : [1, 1.15, 1],
          }}
          transition={{ 
            duration: isHovering ? 2 : 3, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        />

        {/* Animated Waveform Bars - Smaller */}
        <div className="absolute inset-0 flex items-center justify-center">
          {waveformBars.map((height, i) => {
            const angle = (i / 24) * 360;
            const radius = 55; // Reduced from 85
            const x = Math.cos((angle * Math.PI) / 180) * radius;
            const y = Math.sin((angle * Math.PI) / 180) * radius;
            
            return (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: '2px', // Reduced from 3px
                  left: '50%',
                  top: '50%',
                  transformOrigin: 'center',
                  transform: `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${angle + 90}deg)`,
                  background: 'linear-gradient(to top, hsl(var(--primary)), hsl(var(--accent)))',
                }}
                animate={{
                  height: isPlaying ? `${height * 20}px` : '8px', // Reduced scale
                  opacity: isPlaying ? [0.4, 1, 0.4] : 0.3,
                }}
                transition={{
                  duration: isPlaying ? 0.3 : 0.5,
                  repeat: isPlaying ? Infinity : 0,
                  repeatType: "reverse",
                  delay: i * 0.02,
                }}
              />
            );
          })}
        </div>

        {/* Main Bubble - Compact */}
        <motion.div
          className="relative w-28 h-28 rounded-full backdrop-blur-xl bg-gradient-to-br from-background/95 via-background/90 to-primary/20 border-2 border-primary/30 overflow-hidden cursor-pointer"
          animate={{
            boxShadow: isPlaying 
              ? [
                  '0 0 20px rgba(var(--primary), 0.3)',
                  '0 0 35px rgba(var(--primary), 0.6)',
                  '0 0 20px rgba(var(--primary), 0.3)',
                ]
              : '0 0 15px rgba(var(--primary), 0.2)',
            scale: isHovering ? 1.05 : 1,
          }}
          transition={{ 
            boxShadow: { duration: 1.5, repeat: Infinity },
            scale: { duration: 0.2 }
          }}
          onClick={(e) => {
            if (!isDragging) {
              onTogglePlayPause?.();
            }
          }}
        >
          {/* Sparkle Particles - Compact */}
          {isPlaying && (
            <>
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-0.5 h-0.5 bg-accent rounded-full"
                  style={{
                    left: '50%',
                    top: '50%',
                  }}
                  animate={{
                    x: [0, Math.cos((i * 60) * Math.PI / 180) * 35],
                    y: [0, Math.sin((i * 60) * Math.PI / 180) * 35],
                    opacity: [1, 0],
                    scale: [1, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                    ease: "easeOut",
                  }}
                />
              ))}
            </>
          )}

          {/* Album Art - Compact */}
          <div className="absolute inset-0 flex items-center justify-center p-5">
            <motion.div 
              className="relative w-full h-full rounded-full overflow-hidden border-2 border-primary/20"
              animate={{ 
                rotate: isPlaying ? 360 : 0,
                scale: isHovering ? 1.1 : 1,
              }}
              transition={{ 
                rotate: { duration: isPlaying ? 20 : 0, repeat: Infinity, ease: "linear" },
                scale: { duration: 0.2 }
              }}
            >
              <img 
                src={imageUrl} 
                alt={trackName}
                className="w-full h-full object-cover"
              />
              {/* Rotating overlay */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-tr from-primary/30 via-transparent to-accent/30"
                animate={{ rotate: isPlaying ? 360 : 0 }}
                transition={{ duration: 8, repeat: isPlaying ? Infinity : 0, ease: "linear" }}
              />
            </motion.div>
          </div>

          {/* Rotating Track Info - Compact */}
          <motion.div
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <div 
              className="bg-background/95 backdrop-blur-xl rounded-full px-2 py-1 border border-primary/30 shadow-lg"
              style={{ transform: 'rotate(0deg)' }} // Keep text upright while parent rotates
            >
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <p className="text-[8px] font-semibold truncate max-w-[70px] text-center leading-tight">
                  {trackName}
                </p>
                <p className="text-[7px] text-muted-foreground truncate max-w-[70px] text-center">
                  {artistName}
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Time and Source Info at Bottom - Compact */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5 pointer-events-none">
            <span className="text-[7px] px-1.5 py-0.5 rounded-full bg-background/95 backdrop-blur-sm border border-primary/20 text-primary font-medium whitespace-nowrap">
              {source === 'spotify' ? '🎵 Spotify' : '▶️ YouTube'}
            </span>
            <span className="text-[7px] text-foreground font-mono bg-background/95 backdrop-blur-sm px-1.5 py-0.5 rounded-full border border-primary/20">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Play/Pause Overlay - Compact */}
          {isHovering && (
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-50 rounded-full"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="bg-background/90 rounded-full p-2.5 border-2 border-primary shadow-lg"
                  >
                    {isPlaying ? (
                      <Pause className="h-6 w-6 text-primary" strokeWidth={2.5} />
                    ) : (
                      <Play className="h-6 w-6 text-primary" strokeWidth={2.5} fill="hsl(var(--primary))" />
                    )}
                  </motion.div>
                </motion.div>
              </TooltipTrigger>
              <TooltipContent>
                {isPlaying ? 'Pause' : 'Play'}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Progress Ring - Compact */}
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="56"
              cy="56"
              r="50"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              className="text-primary/20"
            />
            <motion.circle
              cx="56"
              cy="56"
              r="50"
              stroke="url(#gradient)"
              strokeWidth="2"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 50}`}
              initial={{ strokeDashoffset: 2 * Math.PI * 50 }}
              animate={{ strokeDashoffset: (1 - progress / 100) * 2 * Math.PI * 50 }}
              transition={{ duration: 0.3 }}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="hsl(var(--primary))" />
                <stop offset="100%" stopColor="hsl(var(--accent))" />
              </linearGradient>
            </defs>
          </svg>

          {/* Close Button - Compact */}
          <Button
            size="icon"
            variant="ghost"
            onClick={onStop}
            className="absolute top-1 right-1 h-5 w-5 rounded-full bg-background/80 hover:bg-destructive/20 hover:text-destructive backdrop-blur-sm z-10"
          >
            <X className="h-2.5 w-2.5" />
          </Button>

          {/* Add to Taste Profile Button for AI Suggestions */}
          {isAISuggestion && !isAdded && onAddToSelections && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleAddToSelections}
                  className="absolute top-1 left-1 h-5 w-5 rounded-full bg-primary/80 hover:bg-primary hover:scale-110 text-primary-foreground backdrop-blur-sm z-10 transition-all"
                >
                  <Plus className="h-2.5 w-2.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Add to Taste Profile
              </TooltipContent>
            </Tooltip>
          )}

          {/* Added Checkmark for AI Suggestions */}
          {isAISuggestion && isAdded && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-1 left-1 h-5 w-5 rounded-full bg-primary backdrop-blur-sm z-10 flex items-center justify-center"
            >
              <Heart className="h-2.5 w-2.5 text-primary-foreground fill-primary-foreground" />
            </motion.div>
          )}
        </motion.div>

        {/* AI Suggestion Add Prompt */}
        <AnimatePresence>
          {showAddPrompt && !isAdded && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.8 }}
              transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
              className="absolute -top-24 left-1/2 -translate-x-1/2 w-64"
            >
              <Card className="p-4 bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-xl border-2 border-primary/40 shadow-[0_0_40px_rgba(var(--primary),0.3)]">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                  className="absolute -top-2 -right-2"
                >
                  <div className="bg-primary rounded-full p-2">
                    <Heart className="h-4 w-4 text-primary-foreground fill-primary-foreground" />
                  </div>
                </motion.div>
                
                <p className="text-sm font-semibold mb-3 text-foreground text-center">
                  ✨ Loving this vibe?
                </p>
                
                <Button
                  onClick={handleAddToSelections}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add to My Selections
                </Button>
                
                <Button
                  onClick={() => setShowAddPrompt(false)}
                  variant="ghost"
                  className="w-full mt-2 text-xs"
                  size="sm"
                >
                  Not this time
                </Button>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Added Confirmation Badge */}
        <AnimatePresence>
          {isAdded && isAISuggestion && (
            <motion.div
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ type: "spring", bounce: 0.6 }}
              className="absolute -top-16 left-1/2 -translate-x-1/2"
            >
              <div className="bg-primary/90 backdrop-blur-sm rounded-full px-4 py-2 border-2 border-primary shadow-lg">
                <p className="text-xs font-bold text-primary-foreground flex items-center gap-2">
                  <Heart className="h-3 w-3 fill-primary-foreground" />
                  Added!
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
    </TooltipProvider>
  );
};
